import 'dayjs/locale/en';
import 'dayjs/locale/pl';

import { DisplayMode, TimeFormat } from '@treneo/shared/constants';
import { useDateTimeConfig } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { ArrowForwardIcon as ArrowForward } from '@treneo/shared/icons';
import { Spacing } from '@treneo/shared/themes';
import type { DateTimeRangeValue } from '@treneo/shared/types';
import { Box, styled } from '@treneo/shared/ui';
import { parseDateString, parseDateTimeString, parseTimeString } from '@treneo/shared/utils';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import type { DateTimePickerRangeProps } from '../constants/date-time-picker-types';
import { useDateTimePickerLogic } from '../hooks/use-date-time-picker-logic';

const DateTimeRangeInputGroup = styled(Box)`
  display: flex;
  gap: ${Spacing.small};
  align-items: flex-end;
  width: 100%;
  min-width: 600px;
  @media (max-width: 768px) {
    min-width: 100%;
    flex-wrap: wrap;
  }
`;

const ArrowContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.small} 0;
  color: ${({ theme }) => theme.palette.text.secondary};
  min-height: 56px;
  flex-shrink: 0;
`;

export const DateTimePickerRange = ({
  value,
  onChange,
  onBlur,
  required = false,
  error = false,
  helperText,
  disabled = false,
  startMinDate,
  startMaxDate,
  endMinDate,
  endMaxDate
}: Readonly<DateTimePickerRangeProps>) => {
  const { t } = useAppTranslations();
  const { timeFormat, displayMode, timeStep, mondayFirst } = useDateTimeConfig();
  const {
    dayjsDateFormat,
    dayjsTimeFormat,
    createDateTimeOnChange,
    createDateOnChange,
    createTimeOnChange
  } = useDateTimePickerLogic();

  const handleGlobalChange = (newValue: DateTimeRangeValue) => {
    onChange(newValue);
  };

  const handleGlobalBlur = () => {
    onBlur?.();
  };

  const commonTextFieldProps = {
    onBlur: handleGlobalBlur
  };

  const startDateTimeOnChange = createDateTimeOnChange(value, handleGlobalChange, 'start');
  const endDateTimeOnChange = createDateTimeOnChange(value, handleGlobalChange, 'end');

  const startDateOnChange = createDateOnChange(value, handleGlobalChange, 'start', true);
  const endDateOnChange = createDateOnChange(value, handleGlobalChange, 'end');

  const startTimeOnChange = createTimeOnChange(value, handleGlobalChange, 'start');
  const endTimeOnChange = createTimeOnChange(value, handleGlobalChange, 'end');

  if (displayMode === DisplayMode.COMBINED) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={mondayFirst ? 'en-GB' : 'en'}>
        <DateTimeRangeInputGroup>
          <MUIDateTimePicker
            label={t('courseForm.startDateTime')}
            value={parseDateTimeString(
              value.startDate,
              value.startTime,
              dayjsDateFormat,
              dayjsTimeFormat
            )}
            onChange={startDateTimeOnChange}
            format={`${dayjsDateFormat} ${dayjsTimeFormat}`}
            ampm={timeFormat === TimeFormat.H12}
            disabled={disabled}
            slotProps={{ textField: { ...commonTextFieldProps, required, error, helperText } }}
          />
          <ArrowContainer>
            <ArrowForward />
          </ArrowContainer>
          <MUIDateTimePicker
            label={t('courseForm.endDateTime')}
            value={parseDateTimeString(
              value.endDate,
              value.endTime,
              dayjsDateFormat,
              dayjsTimeFormat
            )}
            onChange={endDateTimeOnChange}
            format={`${dayjsDateFormat} ${dayjsTimeFormat}`}
            ampm={timeFormat === TimeFormat.H12}
            disabled={disabled}
            slotProps={{ textField: { ...commonTextFieldProps, required, error } }}
          />
        </DateTimeRangeInputGroup>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={mondayFirst ? 'en-GB' : 'en'}>
      <DateTimeRangeInputGroup>
        <DatePicker
          label={t('courseForm.startDate')}
          value={parseDateString(value.startDate, dayjsDateFormat)}
          onChange={startDateOnChange}
          format={dayjsDateFormat}
          minDate={
            startMinDate ? (parseDateString(startMinDate, dayjsDateFormat) ?? undefined) : undefined
          }
          maxDate={
            startMaxDate ? (parseDateString(startMaxDate, dayjsDateFormat) ?? undefined) : undefined
          }
          disabled={disabled}
          slotProps={{ textField: { ...commonTextFieldProps, required, error, helperText } }}
        />
        <TimePicker
          label={t('courseForm.startTime')}
          value={parseTimeString(value.startTime, dayjsTimeFormat)}
          onChange={startTimeOnChange}
          format={dayjsTimeFormat}
          ampm={timeFormat === TimeFormat.H12}
          timeSteps={{ minutes: timeStep }}
          disabled={disabled}
          slotProps={{ textField: { ...commonTextFieldProps, error } }}
        />
        <ArrowContainer>
          <ArrowForward />
        </ArrowContainer>
        <DatePicker
          label={t('courseForm.endDate')}
          value={parseDateString(value.endDate, dayjsDateFormat)}
          onChange={endDateOnChange}
          format={dayjsDateFormat}
          minDate={
            endMinDate ? (parseDateString(endMinDate, dayjsDateFormat) ?? undefined) : undefined
          }
          maxDate={
            endMaxDate ? (parseDateString(endMaxDate, dayjsDateFormat) ?? undefined) : undefined
          }
          disabled={disabled}
          slotProps={{ textField: { ...commonTextFieldProps, required, error } }}
        />
        <TimePicker
          label={t('courseForm.endTime')}
          value={parseTimeString(value.endTime, dayjsTimeFormat)}
          onChange={endTimeOnChange}
          format={dayjsTimeFormat}
          ampm={timeFormat === TimeFormat.H12}
          timeSteps={{ minutes: timeStep }}
          disabled={disabled}
          slotProps={{ textField: { ...commonTextFieldProps, error } }}
        />
      </DateTimeRangeInputGroup>
    </LocalizationProvider>
  );
};
